/**
 * Shared validation utilities for Omnio View frontend.
 * Mirrors backend validators.py rules so client-side checks
 * match server-side validation.
 */

export const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const PROJECT_KEY_RE = /^[A-Z][A-Z0-9_]{1,19}$/;

export const MAX_LENGTHS = {
  prompt: 2000,
  summary: 500,
  description: 10000,
  email: 254,
  password: 128,
  fullName: 200,
  feedbackMessage: 2000,
};

export const MIN_LENGTHS = {
  password: 10,
};

export function isValidEmail(email) {
  return !!email && EMAIL_RE.test(email) && email.length <= MAX_LENGTHS.email;
}

export function isValidProjectKey(key) {
  return PROJECT_KEY_RE.test(key);
}

/**
 * Validate a named field and return an error message string, or null if valid.
 */
// Feedback categories and labels
export const FEEDBACK_CATEGORIES = ['bug', 'feature_request', 'usability', 'general'];

export const FEEDBACK_CATEGORY_LABELS = {
  bug: 'Bug Report',
  feature_request: 'Feature Request',
  usability: 'Usability Issue',
  general: 'General Feedback',
};

// SCRUM-504: transcript uploads (mirrors backend services/transcript_service.py)
export const TRANSCRIPT_EXTENSIONS = ['.txt', '.vtt', '.srt', '.docx'];
export const MAX_TRANSCRIPT_BYTES = 5 * 1024 * 1024;

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate a transcript file before upload. Returns an error message, or null if valid.
 */
export function validateTranscriptFile(file) {
  if (!file) return 'No file selected';
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot) : '';
  if (!TRANSCRIPT_EXTENSIONS.includes(ext))
    return `Unsupported file type. Upload a ${TRANSCRIPT_EXTENSIONS.join(', ')} transcript.`;
  if (file.size === 0) return 'The file is empty.';
  if (file.size > MAX_TRANSCRIPT_BYTES)
    return `File is too large (max ${formatFileSize(MAX_TRANSCRIPT_BYTES)}).`;
  return null;
}

export function validateFeedbackMessage(message) {
  const v = (message || '').trim();
  if (!v) return 'Please enter your feedback';
  if (v.length > MAX_LENGTHS.feedbackMessage)
    return `Feedback must be under ${MAX_LENGTHS.feedbackMessage} characters`;
  return null;
}

export function validateField(name, value) {
  const v = (value || '').trim();
  switch (name) {
    case 'email':
      if (!v) return 'Email is required';
      if (!isValidEmail(v)) return 'Please enter a valid email address';
      return null;
    case 'password':
      if (!v) return 'Password is required';
      if (v.length < MIN_LENGTHS.password)
        return `Password must be at least ${MIN_LENGTHS.password} characters`;
      if (v.length > MAX_LENGTHS.password)
        return `Password must be at most ${MAX_LENGTHS.password} characters`;
      return null;
    case 'fullName':
      if (v && v.length > MAX_LENGTHS.fullName)
        return `Name must be at most ${MAX_LENGTHS.fullName} characters`;
      return null;
    default:
      return null;
  }
}
